import { useState, useEffect } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/hooks/use-toast';
import MapGL, { Marker } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import PhoneNumberDisplay from '@/components/ui/PhoneNumberDisplay';
import { useFetchListProvinces, useFetchAreaByProvinceCode } from '@/hooksApi/useRegion';
import { attachCountryCodeToPhone, parsePhoneNumber } from '@/utils';
import { MAPBOX_TOKEN } from '@/hooks/useMapCoordinates';
import type { Contact, ContactRequest } from '@/types/contacts';

/* ── Map picker ── */
interface MapPickerProps {
  lat: number;
  lng: number;
  onLocationChange: (lat: number, lng: number) => void;
}

const MapPicker = ({ lat, lng, onLocationChange }: MapPickerProps) => {
  const [viewport, setViewport] = useState({ latitude: lat, longitude: lng, zoom: 12 });

  useEffect(() => {
    setViewport((v) => ({ ...v, latitude: lat, longitude: lng }));
  }, [lat, lng]);

  return (
    <MapGL
      {...viewport}
      style={{ width: '100%', height: 280, borderRadius: 8 }}
      mapStyle='mapbox://styles/mapbox/streets-v11'
      mapboxAccessToken={MAPBOX_TOKEN}
      onMove={(e) => setViewport(e.viewState)}
      onClick={(e) => onLocationChange(e.lngLat.lat, e.lngLat.lng)}
    >
      <Marker
        latitude={lat}
        longitude={lng}
        draggable
        onDragEnd={(e) => onLocationChange(e.lngLat.lat, e.lngLat.lng)}
      />
    </MapGL>
  );
};

/* ── Form state ── */
export interface ContactFormState {
  full_name: string;
  countryCode: string;
  phone: string;
  province: string;
  region_uid: string;
  address_text: string;
  secondary_phone: string;
  secondary_countryCode: string;
  lat: number;
  lng: number;
}

export const EMPTY_CONTACT_FORM: ContactFormState = {
  full_name: '',
  countryCode: '964',
  phone: '',
  province: '',
  region_uid: '',
  address_text: '',
  secondary_phone: '',
  secondary_countryCode: '964',
  lat: 33.3152,
  lng: 44.3661,
};

export const buildPayload = (form: ContactFormState): ContactRequest => ({
  full_name: form.full_name,
  phone: attachCountryCodeToPhone(form.phone, form.countryCode) ?? '',
  address_text: form.address_text,
  region_uid: form.region_uid,
  lat: String(form.lat),
  lng: String(form.lng),
  ...(form.secondary_phone.trim()
    ? { additional_phones: [attachCountryCodeToPhone(form.secondary_phone, form.secondary_countryCode) ?? ''] }
    : {}),
});

export const prefillFromApi = (contact: Contact): ContactFormState => {
  const phoneInfo = parsePhoneNumber(contact.phone);
  const provinceCode = contact.region?.province_code ?? '';

  const secondaryRaw = contact.additional_phones?.[0] ?? '';
  const secondaryInfo = secondaryRaw ? parsePhoneNumber(secondaryRaw) : null;

  return {
    full_name: contact.full_name ?? '',
    countryCode: phoneInfo.countryCode ?? '964',
    phone: phoneInfo.phone ?? '',
    province: provinceCode,
    region_uid: contact.region?.uid ?? '',
    address_text: contact.address_text ?? '',
    secondary_phone: secondaryInfo?.phone ?? '',
    secondary_countryCode: secondaryInfo?.countryCode ?? '964',
    lat: contact.lat ? +contact.lat : 33.3152,
    lng: contact.lng ? +contact.lng : 44.3661,
  };
};

/* ── Props ── */
interface ContactFormProps {
  heading: string;
  initialForm?: ContactFormState;
  isSaving: boolean;
  onSave: (form: ContactFormState) => void;
  isEditMode?: boolean;
}

/* ── Component ── */
const ContactForm = ({
  heading,
  initialForm,
  isSaving,
  onSave,
  isEditMode = false,
}: ContactFormProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [form, setForm] = useState<ContactFormState>(initialForm ?? EMPTY_CONTACT_FORM);
  const [selectedProvince, setSelectedProvince] = useState(initialForm?.province ?? '');
  const [fieldErrors, setFieldErrors] = useState<Record<string, boolean>>({});

  /* Sync when initialForm arrives (edit prefill) */
  useEffect(() => {
    if (initialForm) {
      setForm(initialForm);
      setSelectedProvince(initialForm.province);
    }
  }, [initialForm]);

  const { data: provinces, isLoading: isLoadingProvinces } = useFetchListProvinces();
  const { data: areas, isInitialLoading: isLoadingAreas } =
    useFetchAreaByProvinceCode(selectedProvince);

  const inputClass = (field?: string) =>
    `w-full h-10 ps-3 pe-3 rounded-lg border text-sm text-sd-foreground focus:outline-none focus:ring-2 focus:ring-sd-ring ${
      field && fieldErrors[field] ? 'border-sd-destructive bg-sd-destructive/5' : 'border-sd-input bg-sd-input-bg'
    }`;

  const handleSave = () => {
    const errors: Record<string, boolean> = {};
    if (!form.full_name.trim()) errors.full_name = true;
    if (!form.phone.trim()) errors.phone = true;
    if (!form.province.trim()) errors.province = true;
    if (!form.region_uid.trim()) errors.area = true;
    if (!form.address_text.trim()) errors.address = true;

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      const missing = Object.keys(errors).map((k) => String(t(k))).join(', ');
      toast({ title: t('required_fields_missing'), description: missing, variant: 'destructive' });
      return;
    }
    onSave(form);
  };

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
        <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
          {/* Header */}
          <div className='px-6 py-4 border-b border-sd-border flex items-center gap-3'>
            <button
              onClick={() => navigate('/settings/address-book')}
              className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
            >
              <ArrowLeft className='h-4 w-4' />
            </button>
            <h2 className='text-sm font-semibold text-sd-foreground m-0'>{heading}</h2>
          </div>

          <div className='p-6 space-y-6'>
            {/* Section 1: Contact Details */}
            <div>
              <p className='text-xs font-semibold text-sd-muted-foreground uppercase tracking-wider mb-3'>
                {t('contact_details')}
              </p>
              <div className='space-y-4'>
                {/* Full Name */}
                <div className='space-y-1.5'>
                  <label className='text-xs font-medium text-sd-muted-foreground'>
                    {t('full_name')} *
                  </label>
                  <input
                    type='text'
                    value={form.full_name}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, full_name: e.target.value }));
                      setFieldErrors((prev) => ({ ...prev, full_name: false }));
                    }}
                    placeholder={String(t('full_name'))}
                    className={inputClass('full_name')}
                  />
                  {fieldErrors.full_name && (
                    <p className='text-xs text-sd-destructive mt-1'>{t('this_field_is_required')}</p>
                  )}
                </div>

                {/* Phone */}
                <div className='space-y-1.5'>
                  <label className='text-xs font-medium text-sd-muted-foreground'>
                    {t('phone_number')} *
                  </label>
                  <div className={fieldErrors.phone ? 'rounded-lg ring-1 ring-sd-destructive' : ''}>
                    <PhoneNumberDisplay
                      phoneCode={`+${form.countryCode}`}
                      nationalPhone={form.phone}
                      onPhoneCodeChange={(value) =>
                        setForm((f) => ({ ...f, countryCode: value.replace(/\D/g, '') }))
                      }
                      onNationalPhoneChange={(value) => {
                        setForm((f) => ({ ...f, phone: value }));
                        setFieldErrors((prev) => ({ ...prev, phone: false }));
                      }}
                      placeholder={String(t('phone_number'))}
                    />
                  </div>
                  {fieldErrors.phone && (
                    <p className='text-xs text-sd-destructive mt-1'>{t('this_field_is_required')}</p>
                  )}
                </div>

                {/* Secondary Phone */}
                <div className='space-y-1.5'>
                  <label className='text-xs font-medium text-sd-muted-foreground'>
                    {t('secondary_phone')}
                  </label>
                  <PhoneNumberDisplay
                    phoneCode={`+${form.secondary_countryCode}`}
                    nationalPhone={form.secondary_phone}
                    onPhoneCodeChange={(value) =>
                      setForm((f) => ({
                        ...f,
                        secondary_countryCode: value.replace(/\D/g, ''),
                      }))
                    }
                    onNationalPhoneChange={(value) =>
                      setForm((f) => ({ ...f, secondary_phone: value }))
                    }
                    placeholder={String(t('secondary_phone'))}
                  />
                </div>
              </div>
            </div>

            <div className='border-t border-sd-border' />

            {/* Section 2: Address Info */}
            <div>
              <p className='text-xs font-semibold text-sd-muted-foreground uppercase tracking-wider mb-3'>
                {t('address_info')}
              </p>
              <div className='space-y-4'>
                {/* Province + Area */}
                <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
                  {/* Province */}
                  <div className='space-y-1.5'>
                    <label className='text-xs font-medium text-sd-muted-foreground'>
                      {t('province')} *
                    </label>
                    <Select
                      value={form.province}
                      onValueChange={(v) => {
                        setSelectedProvince(v);
                        setForm((f) => ({ ...f, province: v, region_uid: '' }));
                        setFieldErrors((prev) => ({ ...prev, province: false }));
                      }}
                      disabled={isLoadingProvinces}
                    >
                      <SelectTrigger
                        className={`h-10 rounded-lg text-sm text-sd-foreground focus:ring-sd-ring ${
                          fieldErrors.province
                            ? 'border-sd-destructive bg-sd-destructive/5'
                            : 'border-sd-input bg-sd-input-bg'
                        }`}
                      >
                        <SelectValue placeholder={String(t('province'))} />
                      </SelectTrigger>
                      <SelectContent>
                        {provinces?.object?.map((p) => (
                          <SelectItem key={p.province_code} value={p.province_code}>
                            {p.province}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.province && (
                      <p className='text-xs text-sd-destructive mt-1'>{t('this_field_is_required')}</p>
                    )}
                  </div>

                  {/* Area */}
                  <div className='space-y-1.5'>
                    <label className='text-xs font-medium text-sd-muted-foreground'>
                      {t('area')} *
                    </label>
                    <Select
                      value={form.region_uid}
                      onValueChange={(v) => {
                        setForm((f) => ({ ...f, region_uid: v }));
                        setFieldErrors((prev) => ({ ...prev, area: false }));
                      }}
                      disabled={!selectedProvince || isLoadingAreas}
                    >
                      <SelectTrigger
                        className={`h-10 rounded-lg text-sm text-sd-foreground focus:ring-sd-ring ${
                          fieldErrors.area
                            ? 'border-sd-destructive bg-sd-destructive/5'
                            : 'border-sd-input bg-sd-input-bg'
                        }`}
                      >
                        <SelectValue placeholder={String(t('area'))} />
                      </SelectTrigger>
                      <SelectContent>
                        {areas?.object?.map((a) => (
                          <SelectItem key={a.uid} value={a.uid}>
                            {a.area_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {fieldErrors.area && (
                      <p className='text-xs text-sd-destructive mt-1'>{t('this_field_is_required')}</p>
                    )}
                  </div>
                </div>

                {/* Address */}
                <div className='space-y-1.5'>
                  <label className='text-xs font-medium text-sd-muted-foreground'>
                    {t('address')} *
                  </label>
                  <input
                    type='text'
                    value={form.address_text}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, address_text: e.target.value }));
                      setFieldErrors((prev) => ({ ...prev, address: false }));
                    }}
                    placeholder={String(t('address'))}
                    className={inputClass('address')}
                  />
                  {fieldErrors.address && (
                    <p className='text-xs text-sd-destructive mt-1'>{t('this_field_is_required')}</p>
                  )}
                </div>

                {/* Map */}
                <div className='space-y-1.5'>
                  <label className='text-xs font-medium text-sd-muted-foreground'>
                    {t('pin_location_on_map')}
                  </label>
                  <p className='text-xs text-sd-muted-foreground'>{t('click_map_or_drag')}</p>
                  <MapPicker
                    lat={form.lat}
                    lng={form.lng}
                    onLocationChange={(lat, lng) => setForm((f) => ({ ...f, lat, lng }))}
                  />
                  <p className='text-xs text-sd-muted-foreground tabular-nums' dir='ltr'>
                    {t('coordinates')}: {form.lat.toFixed(5)}, {form.lng.toFixed(5)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className='px-6 py-4 border-t border-sd-border flex justify-end gap-3'>
            <button
              onClick={() => navigate('/settings/address-book')}
              className='px-4 py-2.5 rounded-lg border border-sd-input bg-sd-card text-sm font-medium hover:bg-sd-muted transition-colors'
            >
              {t('cancel')}
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className='flex items-center gap-2 px-6 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-60'
            >
              {isSaving && <Loader2 className='h-4 w-4 animate-spin' />}
              {isSaving
                ? String(t('saving'))
                : String(isEditMode ? t('update_contact') : t('save_contact'))}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactForm;
